import { apiClient } from '@/api/client';
import type { ApiResponseWrapper } from '@/types/api.types';

export type HrCaseKind = 'discipline' | 'grievance';

export type HrCaseStatus =
  | 'open'
  | 'investigating'
  | 'hearing_scheduled'
  | 'decided'
  | 'appeal_requested'
  | 'appeal_decided'
  | 'closed'
  | 'withdrawn'
  | 'submitted'
  | 'blocked'
  | 'acknowledged'
  | 'findings_issued';

export type HrCaseMemberRole =
  | 'investigator'
  | 'decision_maker'
  | 'appeal_officer'
  | 'handler'
  | 'observer';

export type DisciplineDecision = 'no_action' | 'warning' | 'dismissal';

export type AppealDecision = 'upheld' | 'varied' | 'overturned';

export type GrievanceFinding = 'upheld' | 'partially_upheld' | 'not_upheld';

export interface HrCaseAction {
  index: number;
  description: string;
  owner: HrCasePerson | null;
  dueAt: string | null;
  completedAt: string | null;
  /** Present only when the viewer can also see the linked disciplinary case. */
  linkedCase?: { _id: string; reference: string };
}

export interface HrCaseAppeal {
  requestedAt: string;
  grounds: string;
  decision?: AppealDecision | null;
  decidedAt?: string | null;
}

export interface HrCasePerson {
  userId: string;
  name: string;
  employeeId?: string;
}

export interface HrCaseOption {
  key: string;
  label: string;
  active: boolean;
}

export interface HrWarningLevel {
  key: string;
  label: string;
  rank: number;
  expiryDays: number;
}

export interface HrCaseSettings {
  companyId: string;
  disciplineCaseTypes: HrCaseOption[];
  warningLevels: HrWarningLevel[];
  activeWarningsBeforeNextLevel: number;
  hearingNoticeDays: number;
  appealWindowDays: number;
  grievanceCategories: HrCaseOption[];
  grievanceIntakeUserIds: string[];
  grievanceAlternateUserIds: string[];
  acknowledgeWithinDays: number;
  /** True while the company has never saved its own settings. */
  isDefault: boolean;
  updatedAt?: string;
}

export type HrCaseSettingsInput = Partial<
  Omit<HrCaseSettings, 'companyId' | 'isDefault' | 'updatedAt'>
>;

export interface HrCaseListItem {
  _id: string;
  reference: string;
  kind: HrCaseKind;
  typeKey: string;
  typeLabel: string;
  status: HrCaseStatus;
  subject: HrCasePerson | null;
  complainant?: HrCasePerson | null;
  myRoles: HrCaseMemberRole[];
  openedAt: string;
  hearingAt?: string;
  decision?: DisciplineDecision;
  warningLevelKey?: string;
  finding?: GrievanceFinding;
  closedAt?: string;
}

export interface HrCasePage {
  data: HrCaseListItem[];
  total: number;
  page: number;
  limit: number;
}

export interface HrCaseMember extends HrCasePerson {
  role: HrCaseMemberRole;
  addedAt: string;
  removedAt: string | null;
}

export interface HrCaseWarning {
  levelKey: string;
  rank: number;
  issuedAt: string;
  expiresAt: string;
  expiredAt?: string;
}

export interface HrCase {
  _id: string;
  reference: string;
  kind: HrCaseKind;
  companyId: string;
  typeKey: string;
  typeLabel: string;
  status: HrCaseStatus;
  /** `team`: on the case team. `oversight`: owner or admin, reading only. */
  viewerAccess: 'team' | 'oversight';
  viewerRoles: HrCaseMemberRole[];
  isMember: boolean;
  subject: HrCasePerson | null;
  complainant: HrCasePerson | null;
  members: HrCaseMember[];
  deadlines: {
    hearingAt: string | null;
    appealClosesAt: string | null;
    acknowledgeBy: string | null;
  };
  outcome: {
    decision?: DisciplineDecision;
    warning?: HrCaseWarning;
    finding?: GrievanceFinding;
    actions?: HrCaseAction[];
    decidedBy?: string;
    decidedAt?: string;
  } | null;
  /** Grievances: whether routing used the intake or the alternate handlers. */
  routing?: {
    route: 'normal' | 'alternate';
    reason?: 'respondent_is_handler' | 'respondent_is_hr' | 'no_intake_handler';
  } | null;
  letter: { documentId: string; status: string; envelopeStatus: string | null } | null;
  appeal: HrCaseAppeal | null;
  /** The subject can still appeal the decision, so the case cannot close yet. */
  appealOpen: boolean;
  /** This case suspended the employee and the suspension has not been lifted. */
  subjectSuspended: boolean;
  lifecycleLinks: Array<{
    command: 'suspend' | 'reinstate' | 'separation' | 'cancel_separation';
    requestId: string;
    at: string;
  }>;
  settings: {
    warningLevels: HrWarningLevel[];
    hearingNoticeDays: number;
    appealWindowDays: number;
  };
  openedBy: HrCasePerson | null;
  openedAt: string;
  closedAt: string | null;
  withdrawnReason: string | null;
}

export interface HrCaseEntry {
  _id: string;
  type:
    | 'note'
    | 'evidence'
    | 'meeting'
    | 'finding'
    | 'decision'
    | 'appeal'
    | 'transition'
    | 'member_change'
    | 'party_response';
  audience: 'team' | 'parties';
  author: HrCasePerson | null;
  addressedTo: HrCasePerson | null;
  body: string | null;
  attachments: Array<{
    index: number;
    originalName: string;
    contentType: string;
    size: number;
    sha256: string;
  }>;
  meeting: { at: string; location?: string } | null;
  fromStatus: HrCaseStatus | null;
  toStatus: HrCaseStatus | null;
  detail: Record<string, unknown>;
  createdAt: string;
}

export interface HrWarningAdvice {
  activeWarnings: Array<{
    caseId: string;
    reference: string;
    levelKey: string;
    expiresAt: string;
  }>;
  suggestedLevelKey: string | null;
  advisoryOnly: true;
}

export interface HrCaseAccessRow {
  _id: string;
  action: 'view' | 'attachment_download' | 'export' | 'command';
  actor: HrCasePerson | null;
  detail: Record<string, unknown>;
  at: string;
}

const base = (companyId: string): string => `/companies/${companyId}/hr/cases`;

function unwrap<T>(body: ApiResponseWrapper<T> | T): T {
  return body && typeof body === 'object' && 'data' in (body as object)
    ? (body as ApiResponseWrapper<T>).data
    : (body as T);
}

export async function getCaseSettings(companyId: string): Promise<HrCaseSettings> {
  const { data } = await apiClient.get<ApiResponseWrapper<HrCaseSettings>>(
    `${base(companyId)}/settings`
  );
  return unwrap(data);
}

export async function updateCaseSettings(
  companyId: string,
  input: HrCaseSettingsInput
): Promise<HrCaseSettings> {
  const { data } = await apiClient.put<ApiResponseWrapper<HrCaseSettings>>(
    `${base(companyId)}/settings`,
    input
  );
  return unwrap(data);
}

export async function listCases(
  companyId: string,
  params: { kind: HrCaseKind; status?: HrCaseStatus; page?: number; limit?: number }
): Promise<HrCasePage> {
  const { data } = await apiClient.get<ApiResponseWrapper<HrCasePage>>(base(companyId), {
    params,
  });
  return unwrap(data);
}

export async function getCase(companyId: string, caseId: string): Promise<HrCase> {
  const { data } = await apiClient.get<ApiResponseWrapper<HrCase>>(
    `${base(companyId)}/${caseId}`
  );
  return unwrap(data);
}

export async function listCaseEntries(companyId: string, caseId: string): Promise<HrCaseEntry[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<HrCaseEntry[]>>(
    `${base(companyId)}/${caseId}/entries`
  );
  return unwrap(data);
}

export async function getWarningAdvice(
  companyId: string,
  caseId: string
): Promise<HrWarningAdvice> {
  const { data } = await apiClient.get<ApiResponseWrapper<HrWarningAdvice>>(
    `${base(companyId)}/${caseId}/warning-advice`
  );
  return unwrap(data);
}

export async function getCaseAccessLog(
  companyId: string,
  caseId: string
): Promise<HrCaseAccessRow[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<HrCaseAccessRow[]>>(
    `${base(companyId)}/${caseId}/access-log`
  );
  return unwrap(data);
}

/** Every command carries a client-generated id so a retried submission is not applied twice. */
interface Idempotent {
  requestId: string;
}

export interface OpenDisciplineInput extends Idempotent {
  subjectUserId: string;
  typeKey: string;
  summary: string;
}

export async function openDisciplineCase(
  companyId: string,
  input: OpenDisciplineInput
): Promise<HrCase> {
  const { data } = await apiClient.post<ApiResponseWrapper<HrCase>>(
    `${base(companyId)}/discipline`,
    input
  );
  return unwrap(data);
}

export interface AddCaseEntryInput extends Idempotent {
  type: 'note' | 'meeting';
  body: string;
  audience: 'team' | 'parties';
  /** Grievances: who a shared entry is addressed to. */
  addressee?: 'complainant' | 'respondent';
  meetingAt?: string;
  location?: string;
  file?: File;
}

export async function addCaseEntry(
  companyId: string,
  caseId: string,
  input: AddCaseEntryInput
): Promise<HrCaseEntry> {
  const { file, ...fields } = input;
  const url = `${base(companyId)}/${caseId}/entries`;
  if (!file) {
    const { data } = await apiClient.post<ApiResponseWrapper<HrCaseEntry>>(url, fields);
    return unwrap(data);
  }
  const form = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    if (value !== undefined) form.append(key, value);
  }
  form.append('file', file);
  const { data } = await apiClient.post<ApiResponseWrapper<HrCaseEntry>>(url, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrap(data);
}

export async function downloadCaseAttachment(
  companyId: string,
  caseId: string,
  entryId: string,
  index: number
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `${base(companyId)}/${caseId}/entries/${entryId}/attachments/${index}`,
    { responseType: 'blob' }
  );
  return data;
}

export async function addCaseMember(
  companyId: string,
  caseId: string,
  input: Idempotent & { userId: string; role: HrCaseMemberRole }
): Promise<HrCase> {
  const { data } = await apiClient.post<ApiResponseWrapper<HrCase>>(
    `${base(companyId)}/${caseId}/members`,
    input
  );
  return unwrap(data);
}

export async function removeCaseMember(
  companyId: string,
  caseId: string,
  userId: string,
  input: Idempotent
): Promise<HrCase | { removed: true }> {
  const { data } = await apiClient.post<ApiResponseWrapper<HrCase | { removed: true }>>(
    `${base(companyId)}/${caseId}/members/${userId}/remove`,
    input
  );
  return unwrap(data);
}

export interface ScheduleHearingInput extends Idempotent {
  at: string;
  location?: string;
  subjectSummary: string;
  overrideReason?: string;
}

export interface DecideDisciplineInput extends Idempotent {
  decision: DisciplineDecision;
  warningLevelKey?: string;
  reason: string;
  subjectSummary: string;
  lastWorkingDate?: string;
}

export interface DecideAppealInput extends Idempotent {
  appealDecision: AppealDecision;
  /** Discipline, `varied`: the new warning level; omitted for no action. */
  warningLevelKey?: string;
  /** Grievance, `varied` or `overturned`: the replacing finding. */
  finding?: GrievanceFinding;
  reason: string;
  subjectSummary: string;
}

export interface GrievanceActionInput {
  description: string;
  ownerUserId?: string;
  dueAt?: string;
  openDisciplineCase?: { typeKey: string; summary: string };
}

export interface IssueFindingsInput extends Idempotent {
  finding: GrievanceFinding;
  reason: string;
  complainantSummary: string;
  actions: GrievanceActionInput[];
}

type CommandBody =
  | { command: 'investigate'; input: Idempotent }
  | { command: 'suspend-subject'; input: Idempotent & { reason: string } }
  | { command: 'reinstate-subject'; input: Idempotent & { reason: string } }
  | { command: 'record-appeal-decision'; input: DecideAppealInput }
  | { command: 'acknowledge'; input: Idempotent & { message?: string } }
  | { command: 'issue-findings'; input: IssueFindingsInput }
  | { command: 'schedule-hearing'; input: ScheduleHearingInput }
  | { command: 'decide'; input: DecideDisciplineInput }
  | { command: 'withdraw'; input: Idempotent & { reason: string } }
  | { command: 'close'; input: Idempotent };

export async function runCaseCommand(
  companyId: string,
  caseId: string,
  body: CommandBody
): Promise<HrCase> {
  const { data } = await apiClient.post<ApiResponseWrapper<HrCase>>(
    `${base(companyId)}/${caseId}/commands/${body.command}`,
    body.input
  );
  return unwrap(data);
}

export async function completeCaseAction(
  companyId: string,
  caseId: string,
  index: number,
  input: Idempotent
): Promise<HrCase> {
  const { data } = await apiClient.post<ApiResponseWrapper<HrCase>>(
    `${base(companyId)}/${caseId}/actions/${index}/complete`,
    input
  );
  return unwrap(data);
}

export async function issueWarningLetter(
  companyId: string,
  caseId: string,
  input: Idempotent & { templateId: string }
): Promise<HrCase> {
  const { data } = await apiClient.post<ApiResponseWrapper<HrCase>>(
    `${base(companyId)}/${caseId}/warning-letter`,
    input
  );
  return unwrap(data);
}

export async function downloadWarningLetter(
  companyId: string,
  caseId: string,
  which: 'original' | 'completed' = 'original'
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `${base(companyId)}/${caseId}/warning-letter/artifact`,
    { params: { which }, responseType: 'blob' }
  );
  return data;
}

// ------------------------------------------------------------ self-service

/** A case as its party sees it: never the team, reasons, or team-only entries. */
export interface MyHrCase {
  _id: string;
  reference: string;
  kind: HrCaseKind;
  companyId: string;
  typeLabel: string;
  status: HrCaseStatus;
  party: 'subject' | 'complainant' | 'respondent' | null;
  hearingAt: string | null;
  outcome: {
    decision?: DisciplineDecision;
    finding?: GrievanceFinding | null;
    decidedAt?: string;
    warning: { levelKey: string; label: string; expiresAt: string; expired: boolean } | null;
  } | null;
  appealClosesAt: string | null;
  canAppeal: boolean;
  /** The complainant can withdraw a grievance until findings are issued. */
  canWithdraw: boolean;
  appeal: HrCaseAppeal | null;
  closedAt: string | null;
}

export interface MyHrCaseDetail extends MyHrCase {
  entries: HrCaseEntry[];
}

export async function listMyCases(): Promise<MyHrCase[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<MyHrCase[]>>('/me/hr-cases');
  return unwrap(data);
}

export async function getMyCase(caseId: string): Promise<MyHrCaseDetail> {
  const { data } = await apiClient.get<ApiResponseWrapper<MyHrCaseDetail>>(
    `/me/hr-cases/${caseId}`
  );
  return unwrap(data);
}

export async function respondToMyCase(
  caseId: string,
  input: Idempotent & { body: string; file?: File }
): Promise<HrCaseEntry> {
  const { file, ...fields } = input;
  const url = `/me/hr-cases/${caseId}/responses`;
  if (!file) {
    const { data } = await apiClient.post<ApiResponseWrapper<HrCaseEntry>>(url, fields);
    return unwrap(data);
  }
  const form = new FormData();
  form.append('body', fields.body);
  form.append('requestId', fields.requestId);
  form.append('file', file);
  const { data } = await apiClient.post<ApiResponseWrapper<HrCaseEntry>>(url, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrap(data);
}

export async function appealMyCase(
  caseId: string,
  input: Idempotent & { grounds: string }
): Promise<MyHrCase> {
  const { data } = await apiClient.post<ApiResponseWrapper<MyHrCase>>(
    `/me/hr-cases/${caseId}/appeal`,
    input
  );
  return unwrap(data);
}

export async function downloadMyCaseAttachment(
  caseId: string,
  entryId: string,
  index: number
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `/me/hr-cases/${caseId}/entries/${entryId}/attachments/${index}`,
    { responseType: 'blob' }
  );
  return data;
}

export async function withdrawMyGrievance(
  caseId: string,
  input: Idempotent & { reason?: string }
): Promise<MyHrCase> {
  const { data } = await apiClient.post<ApiResponseWrapper<MyHrCase>>(
    `/me/hr-cases/${caseId}/withdraw`,
    input
  );
  return unwrap(data);
}

export async function getGrievanceCategories(
  companyId: string
): Promise<Array<{ key: string; label: string }>> {
  const { data } = await apiClient.get<ApiResponseWrapper<Array<{ key: string; label: string }>>>(
    '/me/hr-cases/grievance-categories',
    { params: { companyId } }
  );
  return unwrap(data);
}

export async function searchGrievanceColleagues(
  companyId: string,
  search: string
): Promise<HrCasePerson[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<HrCasePerson[]>>(
    '/me/hr-cases/colleagues',
    { params: { companyId, search } }
  );
  return unwrap(data);
}

export interface SubmitGrievanceInput extends Idempotent {
  companyId: string;
  categoryKey: string;
  respondentUserId?: string;
  respondentIsHr?: boolean;
  description: string;
  file?: File;
}

export async function submitGrievance(input: SubmitGrievanceInput): Promise<MyHrCase> {
  const { file, ...fields } = input;
  const url = '/me/hr-cases/grievances';
  if (!file) {
    const { data } = await apiClient.post<ApiResponseWrapper<MyHrCase>>(url, fields);
    return unwrap(data);
  }
  const form = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    if (value !== undefined) form.append(key, String(value));
  }
  form.append('file', file);
  const { data } = await apiClient.post<ApiResponseWrapper<MyHrCase>>(url, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrap(data);
}

// ------------------------------------------------------------ organization

export interface CasesNeedingMe {
  total: number;
  discipline: number;
  grievance: number;
  /** The longest-waiting cases first, at most ten. */
  cases: Array<{
    _id: string;
    reference: string;
    kind: HrCaseKind;
    status: HrCaseStatus;
    companyId: string;
  }>;
}

/** Cases waiting on the caller as a team member; never cases they only oversee. */
export async function getCasesNeedingMe(): Promise<CasesNeedingMe> {
  const { data } = await apiClient.get<ApiResponseWrapper<CasesNeedingMe>>(
    '/hr/cases/needing-me'
  );
  return unwrap(data);
}

export interface OrganizationAccessRow extends HrCaseAccessRow {
  case: { _id: string; reference: string; kind: HrCaseKind; companyId: string } | null;
}

export async function getOrganizationCaseAccessLog(params: {
  page?: number;
  limit?: number;
}): Promise<{ data: OrganizationAccessRow[]; page: number; limit: number; hasMore: boolean }> {
  const { data } = await apiClient.get<
    ApiResponseWrapper<{
      data: OrganizationAccessRow[];
      page: number;
      limit: number;
      hasMore: boolean;
    }>
  >('/hr/cases/access-log', { params });
  return unwrap(data);
}

/** The case record as a PDF; the server logs every export. */
export async function downloadCaseExport(companyId: string, caseId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`${base(companyId)}/${caseId}/export`, {
    responseType: 'blob',
  });
  return data;
}
